import React, { useEffect, useState } from "react";
import { Inbox, CheckCircle2, Clock, Search, Filter, Plus, ArrowRight, ChevronRight, Calendar, User, Tag, List, LayoutGrid, Check } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, Badge, BtnPrimary } from "./ui";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { EditDialog } from "./CockpitEdit";
import { Spinner, Empty } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const TONE_HEX = { blue: "#3B82F6", purple: "#8B5CF6", green: "#22C55E", amber: "#F59E0B", red: "#EF4444" };
const IN_ARBEIT = ["In Bearbeitung", "Rückmeldung folgt", "Warten auf Rückmeldung"];
const DONE = ["Erledigt", "Abgeschlossen"];
const PRIO_FILTERS = [["alle", "Alle Prioritäten"], ["hoch", "Hoch"], ["mittel", "Mittel"], ["niedrig", "Niedrig"]];

const statusTone = (l, accent) => {
  if (l.status_key === "neu") return { hex: accent, tone: "green", label: "Neu eingegangen" };
  if (DONE.includes(l.status)) return { hex: TONE_HEX.purple, tone: "slate", label: l.status };
  if (l.status === "Warten auf Rückmeldung" || l.status === "Rückmeldung folgt") return { hex: TONE_HEX.amber, tone: "amber", label: l.status };
  return { hex: TONE_HEX.blue, tone: "blue", label: l.status };
};
const prioTone = (p) => (p === "hoch" ? "red" : p === "mittel" ? "amber" : "slate");

const fmtDT = (iso) => {
  try {
    const d = new Date(iso); const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return ""; }
};

export default function CLeads() {
  const { accent, clientId } = useCockpit();
  const { t } = useI18n();
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [prio, setPrio] = useState("alle");
  const [q, setQ] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [view, setView] = useState("list");
  const [newOpen, setNewOpen] = useState(false);

  const load = () => api.get(`/cockpit/leads${clientId ? `?client_id=${clientId}` : ""}`).then((r) => setLeads(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  if (!leads) return <Spinner />;

  const neu = leads.filter((l) => l.status_key === "neu").length;
  const inArbeit = leads.filter((l) => IN_ARBEIT.includes(l.status)).length;
  const erledigt = leads.filter((l) => DONE.includes(l.status)).length;

  let shown = leads;
  if (filter === "neu") shown = shown.filter((l) => l.status_key === "neu");
  else if (filter === "arbeit") shown = shown.filter((l) => IN_ARBEIT.includes(l.status));
  else if (filter === "erledigt") shown = shown.filter((l) => DONE.includes(l.status));
  if (prio !== "alle") shown = shown.filter((l) => (l.priority || "") === prio);
  if (q.trim()) { const qq = q.toLowerCase(); shown = shown.filter((l) => (l.subject || "").toLowerCase().includes(qq) || (l.message || "").toLowerCase().includes(qq)); }
  shown = [...shown].sort((a, b) => (sortDesc ? (b.created_at || "").localeCompare(a.created_at || "") : (a.created_at || "").localeCompare(b.created_at || "")));

  const noFilters = filter === "alle" && prio === "alle" && !q.trim();
  const submitNew = async ({ subject, message }) => { await api.post(`/cockpit/leads${clientId ? `?client_id=${clientId}` : ""}`, { subject, message }); await load(); };

  const TABS = [["alle", "Alle", null], ["neu", "Neu eingegangen", Inbox], ["arbeit", "In Bearbeitung", Clock], ["erledigt", "Erledigt", CheckCircle2]];

  return (
    <div className="fade-in space-y-5">
      {/* Kopf + Aktionsleiste */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><Inbox className="w-4 h-4" /> {t("Posteingang")}</p>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Anfragen")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Ihre Anfragen und deren aktueller Stand auf einen Blick.")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cleads-search" placeholder={t("Anfragen durchsuchen…")}
              className="pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-gray-500 outline-none focus:border-white/30 transition-colors w-full sm:w-60" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="cleads-filter-btn" className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-semibold text-gray-200 hover:bg-white/10 transition-colors flex items-center gap-2">
                <Filter className="w-4 h-4" /> {t("Filter")}{prio !== "alle" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 bg-[#16161c] border-white/10 text-gray-300 p-1.5" data-testid="cleads-filter-menu">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 px-2 py-1.5">{t("Nach Priorität")}</div>
              {PRIO_FILTERS.map(([k, l]) => (
                <button key={k} onClick={() => setPrio(k)} data-testid={`cleads-prio-${k}`} className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm hover:bg-white/5 transition-colors">
                  <span className={prio === k ? "font-semibold text-white" : ""}>{t(l)}</span>
                  {prio === k && <Check className="w-4 h-4" style={{ color: accent }} />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <BtnPrimary accent={accent} data-testid="cleads-new-btn" onClick={() => setNewOpen(true)} className="!py-2.5"><Plus className="w-4 h-4" /> {t("Neue Anfrage")}</BtnPrimary>
        </div>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard tone="lime" icon={Inbox} value={neu} label="Neu eingegangen" sub="noch nicht bearbeitet" onView={() => setFilter("neu")} active={neu > 0} accent={accent} t={t} testid="cleads-kpi-new" />
        <StatCard tone="blue" icon={Clock} value={inArbeit} label="In Bearbeitung" sub="laufend" onView={() => setFilter("arbeit")} accent={accent} t={t} testid="cleads-kpi-open" />
        <StatCard tone="purple" icon={CheckCircle2} value={erledigt} label="Erledigt" sub="abgeschlossen" onView={() => setFilter("erledigt")} accent={accent} t={t} testid="cleads-kpi-done" />
      </div>

      {/* Filter-Tabs */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="cleads-tabs">
        {TABS.map(([k, label, Icon]) => {
          const on = filter === k;
          if (k === "alle") return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`cleads-tab-${k}`} className="relative px-3 py-2 text-sm font-bold transition-colors" style={{ color: on ? accent : "#9ca3af" }}>
              {t(label)}{on && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full" style={{ background: accent }} />}
            </button>
          );
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`cleads-tab-${k}`} className="px-3.5 py-2 rounded-lg text-sm font-semibold border flex items-center gap-2 transition-colors"
              style={on ? { background: `${accent}1A`, color: accent, borderColor: `${accent}66` } : { color: "#9ca3af", borderColor: "rgba(255,255,255,0.1)" }}>
              {Icon && <Icon className="w-4 h-4" />} {t(label)}
            </button>
          );
        })}
      </div>

      {/* Ergebnis-Container */}
      <Card className="p-5" data-testid="cleads-results">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><Inbox className="w-5 h-5" /></span>
            <div>
              <div className="font-bold" data-testid="cleads-count">{shown.length} {shown.length === 1 ? t("Anfrage") : t("Anfragen")}</div>
              <div className="text-xs text-gray-500">{sortDesc ? t("Sortiert nach neueste zuerst") : t("Sortiert nach älteste zuerst")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="cleads-sort-btn" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold text-gray-200 hover:bg-white/10 transition-colors flex items-center gap-2">
                  {t("Sortieren")}: {sortDesc ? t("Neueste zuerst") : t("Älteste zuerst")}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48 bg-[#16161c] border-white/10 text-gray-300 p-1.5" data-testid="cleads-sort-menu">
                {[[true, "Neueste zuerst"], [false, "Älteste zuerst"]].map(([v, l]) => (
                  <button key={String(v)} onClick={() => setSortDesc(v)} className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm hover:bg-white/5 transition-colors">
                    <span className={sortDesc === v ? "font-semibold text-white" : ""}>{t(l)}</span>
                    {sortDesc === v && <Check className="w-4 h-4" style={{ color: accent }} />}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              <button onClick={() => setView("list")} data-testid="cleads-view-list" className="p-2 transition-colors" style={view === "list" ? { background: `${accent}1A`, color: accent } : { color: "#9ca3af" }}><List className="w-4 h-4" /></button>
              <button onClick={() => setView("grid")} data-testid="cleads-view-grid" className="p-2 transition-colors border-l border-white/10" style={view === "grid" ? { background: `${accent}1A`, color: accent } : { color: "#9ca3af" }}><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {shown.length === 0 ? (
          <Empty text={t("Keine Anfragen in dieser Ansicht.")} hint={t("Neue Anfragen über Ihr Kontaktformular erscheinen hier automatisch.")} />
        ) : (
          <div className={view === "grid" ? "grid grid-cols-1 xl:grid-cols-2 gap-3" : "space-y-3"}>
            {shown.map((l) => <LeadCard key={l.id} l={l} accent={accent} t={t} />)}
          </div>
        )}
      </Card>

      {/* Abschluss-Hinweis */}
      {shown.length > 0 && noFilters && (
        <Card className="p-6 flex flex-col items-center text-center" data-testid="cleads-uptodate">
          <span className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: `${accent}14`, color: accent }}><Inbox className="w-7 h-7" /></span>
          <div className="text-sm text-gray-300">{shown.length === 1 ? t("Nur 1 Anfrage vorhanden") : `${shown.length} ${t("Anfragen vorhanden")}`}</div>
          <div className="text-xs text-gray-500 mt-0.5">{t("Sie sind auf dem neuesten Stand.")}</div>
        </Card>
      )}

      <EditDialog open={newOpen} onClose={() => setNewOpen(false)} title="Neue Anfrage" accent={accent} testid="cleads-new-dialog" successMsg="Anfrage gesendet"
        onSave={submitNew}
        initial={{ subject: "", message: "" }}
        fields={[
          { key: "subject", label: "Betreff", type: "text", placeholder: "Worum geht es?", required: true },
          { key: "message", label: "Nachricht", type: "textarea", rows: 4, placeholder: "Beschreiben Sie Ihr Anliegen …" },
        ]} />
    </div>
  );
}

const StatCard = ({ tone, icon: Icon, value, label, sub, onView, active, accent, t, testid }) => {
  const c = tone === "lime" ? accent : (TONE_HEX[tone] || accent);
  return (
    <div data-testid={testid} className="relative overflow-hidden rounded-2xl border p-5 lg:p-6 min-h-[180px] flex flex-col justify-between bg-[#121217]"
      style={{ borderColor: active ? `${c}66` : "rgba(255,255,255,0.06)", boxShadow: active ? `inset 0 0 0 1px ${c}22` : "none" }}>
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: c }} />
      <div className="relative flex items-start gap-4">
        <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${c}1F`, color: c }}><Icon className="w-7 h-7" /></span>
        <div className="text-5xl font-black tracking-tight leading-none pt-1">{value}</div>
      </div>
      <div className="relative mt-2">
        <div className="font-bold text-lg leading-tight">{t(label)}</div>
        <div className="text-sm text-gray-500">{t(sub)}</div>
      </div>
      <button onClick={onView} data-testid={`${testid}-view`} className="relative mt-3 text-sm font-bold flex items-center gap-1 w-fit hover:gap-2 transition-all" style={{ color: c }}>{t("Ansehen")} <ArrowRight className="w-4 h-4" /></button>
    </div>
  );
};

const LeadCard = ({ l, accent, t }) => {
  const st = statusTone(l, accent);
  return (
    <div className="relative rounded-xl border border-white/10 bg-white/[0.02] p-4 lg:p-5 pl-6 overflow-hidden hover:border-white/20 transition-colors" data-testid={`clead-${l.id}`}>
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: st.hex }} />
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${st.hex}1A`, color: st.hex }}><Inbox className="w-6 h-6" /></span>
        <div className="flex-1 min-w-0">
          <Badge tone={st.tone} accent={accent}>{t(st.label)}</Badge>
          <div className="font-bold text-[17px] mt-1.5 leading-snug">{l.subject}</div>
          {l.message && <p className="text-sm text-gray-400 mt-1 leading-relaxed">{l.message}</p>}
          <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap mt-3 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDT(l.created_at)}</span>
            {l.company && <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {l.company}</span>}
            {l.category && <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-white/10"><Tag className="w-3 h-3" /> {l.category}</span>}
          </div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-3 shrink-0 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Priorität")}</div>
            <Badge tone={prioTone(l.priority)} accent={accent}>{l.priority_label || "—"}</Badge>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{t("Status")}</div>
            <div className="text-sm font-semibold">{t(st.label)}</div>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-600 shrink-0 self-center" />
      </div>
    </div>
  );
};
